<script setup lang="ts">
import { computed } from 'vue'
import { NXGDSTextField, NXGDSButtonVariants } from '@nsftx/seven-nxg-design-system'
import { useCalculatorCashFlow } from '@/composables/useCalculatorCashFlow/useCalculatorCashFlow'
import { useCalculatorMoneyInField } from '@/composables/useCalculatorMoneyInField/useCalculatorMoneyInField'
import { useCalculatorStore } from '@/stores/calculator/calculatorStore'

export type SSCalculatorSummaryLayout = 'inline' | 'multi-row' | 'transactions'

interface SSCalculatorSummaryBarProps {
  showResultAction?: boolean
  layout?: SSCalculatorSummaryLayout
}

const { showResultAction = true, layout = 'inline' } = defineProps<SSCalculatorSummaryBarProps>()

const { formattedPayments, formattedPayouts, restLabel, restAmountWithCurrency, restVariant } =
  useCalculatorCashFlow()

const { moneyInField, setMoneyInField } = useCalculatorMoneyInField()

const { transactionCount } = useCalculatorStore()

const resultButtonVariant = computed(() => {
  switch (restVariant.value) {
    case 'give_to_player':
      return NXGDSButtonVariants.invertedWarning
    case 'collect_from_player':
      return NXGDSButtonVariants.invertedNegative
    default:
      return NXGDSButtonVariants.secondary
  }
})
</script>

<template>
  <div v-if="layout === 'multi-row'" class="ss-calculator-summary ss-calculator-summary--multi-row">
    <div class="ss-calculator-summary__row">
      <slot name="leading" />
      <div class="ss-calculator-summary__metric ss-calculator-summary__metric--money-in">
        <NXGDSTextField
          :model-value="moneyInField"
          @update:model-value="setMoneyInField"
          name="moneyIn"
          type="number"
          :label="$t('calculator_money_in')"
        />
      </div>
    </div>

    <div class="ss-calculator-summary__row ss-calculator-summary__row--halves">
      <div class="ss-calculator-summary__metric">
        <span class="ss-calculator-summary__metric-label">{{ $t('calculator_payouts') }}</span>
        <span class="ss-calculator-summary__metric-value">{{ formattedPayouts }}</span>
      </div>
      <div class="ss-calculator-summary__metric ss-calculator-summary__metric--payments">
        <span class="ss-calculator-summary__metric-label">{{ $t('calculator_payments') }}</span>
        <span class="ss-calculator-summary__metric-value">{{ formattedPayments }}</span>
      </div>
    </div>

    <div
      v-if="showResultAction"
      class="ss-calculator-summary__result"
      :class="resultButtonVariant"
      width="100%"
      tabindex="-1"
    >
      <span class="ss-calculator-summary__result-content">
        <span class="ss-calculator-summary__result-label">{{ restLabel }}</span>
        <span class="ss-calculator-summary__result-value">{{ restAmountWithCurrency }}</span>
      </span>
    </div>
  </div>

  <div
    v-else-if="layout === 'transactions'"
    class="ss-calculator-summary ss-calculator-summary--transactions"
  >
    <div class="ss-calculator-summary__metrics-group">
      <div class="ss-calculator-summary__metrics-primary">
        <div class="ss-calculator-summary__metric ss-calculator-summary__metric--money-in">
          <NXGDSTextField
            :model-value="moneyInField"
            @update:model-value="setMoneyInField"
            name="moneyIn"
            type="number"
            :label="$t('calculator_money_in')"
          />
        </div>

        <div class="ss-calculator-summary__metric">
          <span class="ss-calculator-summary__metric-label">{{ $t('calculator_payments') }}</span>
          <span class="ss-calculator-summary__metric-value">{{ formattedPayments }}</span>
        </div>

        <div class="ss-calculator-summary__metric">
          <span class="ss-calculator-summary__metric-label">{{ $t('calculator_payouts') }}</span>
          <span class="ss-calculator-summary__metric-value">{{ formattedPayouts }}</span>
        </div>
      </div>

      <div
        v-if="showResultAction"
        class="ss-calculator-summary__result"
        :class="resultButtonVariant"
      >
        <span class="ss-calculator-summary__result-content">
          <span class="ss-calculator-summary__result-label">{{ restLabel }}</span>
          <span class="ss-calculator-summary__result-value">{{ restAmountWithCurrency }}</span>
        </span>
      </div>
    </div>
  </div>

  <div v-else class="ss-calculator-summary ss-calculator-summary--inline">
    <div class="ss-calculator-summary__metrics">
      <div class="ss-calculator-summary__metric ss-calculator-summary__metric--money-in">
        <NXGDSTextField
          :model-value="moneyInField"
          @update:model-value="setMoneyInField"
          class="ss-calculator-summary__money-in-input"
          name="moneyIn"
          :label="$t('calculator_money_in')"
        />
      </div>

      <div class="ss-calculator-summary__metric" v-if="transactionCount">
        <span class="ss-calculator-summary__metric-label">{{ $t('calculator_payments') }}</span>
        <span class="ss-calculator-summary__metric-value">{{ formattedPayments }}</span>
      </div>

      <div class="ss-calculator-summary__metric" v-if="transactionCount">
        <span class="ss-calculator-summary__metric-label">{{ $t('calculator_payouts') }}</span>
        <span class="ss-calculator-summary__metric-value">{{ formattedPayouts }}</span>
      </div>
    </div>

    <div
      v-if="showResultAction && transactionCount"
      class="ss-calculator-summary__result"
      :class="resultButtonVariant"
    >
      <span class="ss-calculator-summary__result-content">
        <span class="ss-calculator-summary__result-label">{{ restLabel }}</span>
        <span class="ss-calculator-summary__result-value">{{ restAmountWithCurrency }}</span>
      </span>
    </div>

    <div v-if="!transactionCount" class="ss-calculator__fill">
      <p v-if="transactionCount === 0" class="ss-calculator__empty">
        {{ $t('calculator_no_transactions') }}
      </p>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/mixins' as mixins;

.ss-calculator-summary {
  &__row {
    display: flex;
    align-items: center;
    gap: var(--nxgds-alias-spacing-md);

    &--halves {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: var(--nxgds-alias-spacing-md);
    }
  }

  &__metrics {
    display: flex;
    align-items: start;
    column-gap: var(--nxgds-alias-spacing-md);
  }

  &__metric {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--nxgds-alias-spacing-3xs);
    flex: 1 1 0;
  }

  &__metric-label {
    color: var(--nxgds-mapped-color-text-secondary);
    @include mixins.typography('label-regular');
    white-space: nowrap;
  }

  &__metric-value {
    color: var(--nxgds-mapped-color-text-primary);
    @include mixins.typography('h4-medium');
    white-space: nowrap;
    line-height: 1;
  }

  &__result {
    padding-block: var(--nxgds-alias-spacing-3xs);
    padding-inline: var(--nxgds-alias-spacing-md);
    pointer-events: none;
  }

  &__result-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--nxgds-alias-spacing-3xs);
    width: 100%;
  }

  &__result-label {
    @include mixins.typography('label-regular');
    line-height: 1;
  }

  &__result-value {
    @include mixins.typography('h4-medium');
    line-height: 1;
  }

  &--transactions {
    width: 100%;

    .ss-calculator-summary__metrics-group {
      display: flex;
      align-items: stretch;
      gap: var(--nxgds-alias-spacing-md);
      padding: var(--nxgds-alias-spacing-xs) var(--nxgds-alias-spacing-sm);
      border-radius: var(--nxgds-alias-radius-corner-md);
      background: var(--nxgds-mapped-color-surface-component-subsection);
    }

    .ss-calculator-summary__metrics-primary {
      display: flex;
      gap: var(--nxgds-alias-spacing-lg);
    }

    .ss-calculator-summary__metrics-primary .ss-calculator-summary__metric {
      flex: 0 0 auto;
    }

    .ss-calculator-summary__metric--money-in {
      width: 8.25rem;
      align-self: stretch;
      display: flex;
      flex-direction: column;
    }

    .ss-calculator-summary__result {
      display: flex;
      margin-left: auto;
      padding: var(--nxgds-alias-spacing-2xs) var(--nxgds-alias-spacing-md);
      border-radius: var(--nxgds-mapped-radius-corner-notification-inline);
      pointer-events: none;

      &.inverted-warning {
        background: var(--nxgds-mapped-color-surface-component-notification-warning);
        border: 1px solid var(--nxgds-mapped-color-border-component-notification-warning);
        color: var(--nxgds-mapped-color-text-component-notification-warning);
      }

      &.inverted-negative {
        background: var(--nxgds-mapped-color-surface-component-notification-negative);
        border: 1px solid var(--nxgds-mapped-color-border-component-notification-negative);
        color: var(--nxgds-mapped-color-text-component-notification-positive);
      }

      &.secondary {
        background: var(--nxgds-mapped-color-surface-component-subsection);
        color: var(--nxgds-mapped-color-text-primary);
      }
    }

    .ss-calculator-summary__result-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--nxgds-alias-spacing-3xs);
      width: 100%;
    }

    .ss-calculator-summary__result-label,
    .ss-calculator-summary__result-value {
      line-height: 1;
    }
  }

  &--inline {
    display: flex;
    align-items: stretch;
    gap: var(--nxgds-alias-spacing-2xs);
    flex: 1 1 0;

    .ss-calculator-summary__result {
      line-height: 1;
      margin-left: auto;
      border-radius: var(--nxgds-mapped-radius-corner-notification-inline);
      color: var(--nxgds-mapped-color-text-component-notification-positive);

      &.inverted-warning {
        border: 1px solid var(--nxgds-mapped-color-border-component-notification-warning);
        background: var(--nxgds-mapped-color-surface-component-notification-warning);
        color: var(--nxgds-mapped-color-text-component-notification-warning);
      }

      &.inverted-negative {
        border: 1px solid var(--nxgds-mapped-color-border-component-notification-negative);
        background: var(--nxgds-mapped-color-surface-component-notification-negative);
        color: var(--nxgds-mapped-color-text-component-notification-positive);
      }
    }
  }

  &--multi-row {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: var(--nxgds-alias-spacing-md);
    width: 100%;

    .ss-calculator-summary__metric--money-in {
      flex: 1 1 0;
    }

    .ss-calculator-summary__metric--payments {
      align-items: flex-end;
    }

    .ss-calculator-summary__result {
      width: 100%;
      padding-inline: 0;
      margin-top: auto;
      margin-bottom: var(--nxgds-alias-spacing-md);

      .ss-calculator-summary__result-content {
        display: flex;
        border-radius: var(--nxgds-mapped-radius-corner-notification-inline);
        height: 24px;
        padding: var(--nxgds-alias-spacing-2xs) var(--nxgds-alias-spacing-sm);
        background: var(--nxgds-mapped-color-surface-component-subsection);
        justify-content: center;
        align-content: space-between;
        flex-shrink: 0;
        flex-wrap: wrap;
        color: var(--nxgds-mapped-color-text-component-notification-positive);
      }

      &.inverted-negative {
        .ss-calculator-summary__result-content {
          border-radius: var(--nxgds-mapped-radius-corner-notification-inline);
          background: var(--nxgds-mapped-color-surface-component-notification-negative);
          color: var(--nxgds-mapped-color-text-component-notification-positive);
        }
      }

      &.inverted-warning {
        .ss-calculator-summary__result-content {
          border-radius: var(--nxgds-mapped-radius-corner-notification-inline);
          border: 1px solid var(--nxgds-mapped-color-border-component-notification-warning);
          background: var(--nxgds-mapped-color-surface-component-notification-warning);
          color: var(--nxgds-mapped-color-text-component-notification-warning);
        }
      }
    }
  }
}

.ss-calculator__fill {
  display: flex;
  padding: var(--nxgds-alias-spacing-xs) var(--nxgds-alias-spacing-md);
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  border-radius: var(--nxgds-mapped-radius-corner-md);
  background: var(--nxgds-mapped-color-surface-component-subsection);
  color: var(--nxgds-mapped-color-text-disabled);
}
</style>
